import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Loading one thing from the API.
 *
 * No data-fetching library: the portal reads a handful of endpoints, none of
 * them shared between screens, and none of them worth a cache that could show
 * somebody a stale compliance deadline. What is worth getting right is the part
 * people skip — a response from a request the user has already navigated away
 * from must not overwrite the screen they are looking at now.
 */

export type AsyncState<T> =
  | { readonly status: "loading"; readonly data: null; readonly error: null }
  | { readonly status: "ready"; readonly data: T; readonly error: null }
  | { readonly status: "failed"; readonly data: null; readonly error: unknown };

export interface AsyncResult<T> {
  readonly state: AsyncState<T>;
  /** Re-runs the loader — after a write, or from a "Try again" button. */
  readonly reload: () => void;
}

const LOADING = { status: "loading", data: null, error: null } as const;

export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>(LOADING);
  const [nonce, setNonce] = useState(0);

  // The loader is rebuilt on every render by its callers; pinning it to the
  // declared dependencies is what stops the effect looping.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loader = useCallback(load, deps);

  /** Incremented per run; a response from an older run is discarded. */
  const generation = useRef(0);
  /** The loader the screen's data came from, to tell a reload from a move. */
  const loaded = useRef<(() => Promise<T>) | null>(null);

  useEffect(() => {
    const run = ++generation.current;
    /*
     * A RELOAD DOES NOT BLANK THE SCREEN.
     *
     * `reload()` is what every panel calls after a write, and dropping back
     * to LOADING unmounted whatever the screen was showing. On the room
     * screen that closed the open surface panel, taking with it the
     * "Confirmed by a surveyor" line the save had just earned: answering a
     * question looked exactly like nothing happening, and a surveyor pressed
     * Save twice on the same floor before giving up on it. What is on screen
     * was true a moment ago and is still the best answer there is until the
     * new read lands, so it stays.
     *
     * Moving to a DIFFERENT thing is not a reload. That data is about
     * something else, and leaving it under the new heading would be a lie,
     * so a changed loader still blanks.
     */
    if (loaded.current !== loader) setState(LOADING);
    loaded.current = loader;

    loader().then(
      (data) => {
        if (generation.current === run) {
          setState({ status: "ready", data, error: null });
        }
      },
      (error: unknown) => {
        if (generation.current === run) {
          setState({ status: "failed", data: null, error });
        }
      },
    );

    return () => {
      // Bumping the generation on unmount stops a late response calling
      // setState on a component that has gone.
      generation.current++;
    };
  }, [loader, nonce]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return useMemo(() => ({ state, reload }), [state, reload]);
}
